import { z } from 'zod';
import { hexColorSchema, hillPositionSchema } from './primitives.js';

/**
 * A frozen copy of a hill chart at a moment in time.
 *
 * We keep only the dot essentials inside `dots` (JSONB column server-side)
 * rather than a full relational clone - snapshots are append-only and we
 * never need to query them by milestone, only by date.
 *
 * We include `color` so that replayed snapshots in the timeline keep their
 * visual identity even if the live dot has been recolored since. We do NOT
 * include `externalScopeUrl` - a past snapshot is a state log, not a
 * navigation surface.
 *
 * Use cases:
 * - Weekly progress reports ("here's where we were last Monday")
 * - Replay timeline (scrub through past snapshots)
 * - Audit trail for CIR ("this work was in progress at this date")
 */
export const snapshotDotSchema = z.object({
  externalScopeId: z.string(),
  externalScopeName: z.string(),
  x: hillPositionSchema,
  color: hexColorSchema,
  completed: z.boolean(),
  /**
   * Frozen copy of the dot's `displayOrder` at capture time. Snapshots are
   * a log: even if the live dot is later deleted and its number reused on
   * a hypothetical future rebalance (which we never do), the historical
   * snapshot must keep the number the user saw at the time.
   */
  displayOrder: z.number().int().positive(),
});
export type SnapshotDot = z.infer<typeof snapshotDotSchema>;

/**
 * Snapshot kind.
 *
 * - `'regular'`: every snapshot captured outside a hill completion - manual
 *   click on the snapshot panel, or daily auto-capture by the scheduler.
 * - `'final'`: the irreversible snapshot taken in the same transaction as
 *   the "Mark as Done" transition. Surfaced in the timeline with a "Final
 *   state" badge so it stands apart from the rest of the log.
 *
 * The naming reflects what the snapshot IS, not how it was triggered: the
 * scheduler runs daily (not weekly), and a future trigger source - e.g. a
 * milestone-completion auto-capture - would still be `'regular'`.
 */
export const snapshotKindSchema = z.enum(['regular', 'final']);
export type SnapshotKind = z.infer<typeof snapshotKindSchema>;

export const snapshotSchema = z.object({
  id: z.string().uuid(),
  hillChartId: z.string().uuid(),
  capturedByUserId: z.string().uuid(),
  capturedAt: z.string().datetime(),
  note: z.string().max(500).nullable(),
  dots: z.array(snapshotDotSchema),
  kind: snapshotKindSchema,
});
export type Snapshot = z.infer<typeof snapshotSchema>;
